import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  Pause,
  Play,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import type { OntologyGenerationStreamState } from "../../hooks/useWorkspaceSemanticContext";
import { Badge, Button, Progress } from "../ui";
import { cn } from "../../shared/lib/cn";
import {
  ontologyBuildStageLabel,
  projectOntologyBuildEvents,
  type OntologyBuildProposal,
  type OntologyGenerationRunEvent,
} from "./ontologyBuildProjection";

interface OntologyBuildExperienceProps {
  stream: OntologyGenerationStreamState;
  hiddenArtifactRefs?: Set<string>;
  onReviewChanges?: () => void;
}

const NODE_POSITIONS = [
  { className: "left-[5%] top-[12%]", x: 14, y: 24 },
  { className: "right-[5%] top-[10%]", x: 86, y: 22 },
  { className: "left-[4%] bottom-[15%]", x: 14, y: 72 },
  { className: "right-[4%] bottom-[14%]", x: 86, y: 72 },
  { className: "left-1/2 top-[3%] -translate-x-1/2", x: 50, y: 15 },
  { className: "left-1/2 bottom-[2%] -translate-x-1/2", x: 50, y: 84 },
];

export function OntologyBuildExperience({
  stream,
  hiddenArtifactRefs = new Set<string>(),
  onReviewChanges,
}: OntologyBuildExperienceProps) {
  const [isPaused, setIsPaused] = useState(false);
  const [visibleEvents, setVisibleEvents] = useState<
    OntologyGenerationRunEvent[]
  >(stream.events);

  useEffect(() => {
    if (!isPaused) setVisibleEvents(stream.events);
  }, [isPaused, stream.events]);

  useEffect(() => {
    if (stream.completion || stream.failure) {
      setVisibleEvents(stream.events);
      setIsPaused(false);
    }
  }, [stream.completion, stream.events, stream.failure]);

  const projection = useMemo(
    () => projectOntologyBuildEvents(visibleEvents),
    [visibleEvents],
  );
  const proposals = projection.proposals.filter(
    (proposal) => !hiddenArtifactRefs.has(proposal.artifactRef),
  );
  const entityProposals = proposals
    .filter((proposal) => proposal.artifactType === "entity")
    .slice(0, NODE_POSITIONS.length);
  const relationshipProposals = proposals.filter(
    (proposal) => proposal.artifactType === "relationship",
  );
  const entityPositionByRef = new Map(
    entityProposals.map((proposal, index) => [
      proposal.artifactRef,
      NODE_POSITIONS[index],
    ]),
  );
  const visibleRelationshipProposals = relationshipProposals.filter(
    (proposal) =>
      proposal.sourceArtifactRef &&
      proposal.targetArtifactRef &&
      entityPositionByRef.has(proposal.sourceArtifactRef) &&
      entityPositionByRef.has(proposal.targetArtifactRef),
  );
  const connectionLabel = buildConnectionLabel(
    stream.connectionState,
    stream.wasRestored,
  );
  const terminal = projection.isCompleted || projection.isFailed;
  const progress = projection.isCompleted ? 100 : projection.progress;

  const resumeVisualUpdates = () => {
    setVisibleEvents(stream.events);
    setIsPaused(false);
  };

  if (stream.connectionState === "permission_denied") {
    return (
      <section
        className="border-b border-red-200 bg-red-50 px-4 py-4 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300"
        aria-label="Ontology generation permission required"
        data-testid="ontology-build-experience"
      >
        <div className="flex items-start gap-3" role="alert">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold">Permission required</p>
            <p className="mt-1 text-xs">
              {stream.errorMessage ??
                "You do not have permission to view ontology generation runs."}
            </p>
          </div>
        </div>
        <span
          className="sr-only"
          data-testid="ontology-build-connection-state"
        >
          Permission required
        </span>
      </section>
    );
  }

  return (
    <section
      className="flex min-h-0 flex-1 flex-col bg-white dark:bg-dark-surface"
      aria-label="Live ontology generation"
      data-testid="ontology-build-experience"
    >
      <div className="grid gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-dark-border dark:bg-dark-surface-2 sm:grid-cols-[minmax(180px,.8fr)_minmax(280px,1.5fr)_auto] sm:items-center">
        <div className="flex min-w-0 items-center gap-2">
          {projection.isFailed ? (
            <AlertTriangle className="h-5 w-5 shrink-0 text-red-500" />
          ) : projection.isCompleted ? (
            <Check className="h-5 w-5 shrink-0 text-green-600" />
          ) : (
            <RefreshCw className="h-5 w-5 shrink-0 text-eliza-red motion-safe:animate-spin motion-reduce:animate-none" />
          )}
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold text-charcoal dark:text-gray-100">
              {projection.latestEvent?.message ??
                "Restoring persisted generation events"}
            </p>
            <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
              Run {stream.runId ?? "—"} ·{" "}
              {ontologyBuildStageLabel(projection.stage)}
            </p>
          </div>
        </div>
        <div>
          <Progress
            value={progress}
            size="sm"
            variant={projection.isFailed ? "warning" : "gradient"}
            indeterminate={!terminal && projection.latestEvent == null}
          />
        </div>
        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 text-xs font-semibold",
              stream.connectionState === "failed"
                ? "text-red-600 dark:text-red-300"
                : stream.connectionState === "polling"
                  ? "text-amber-700 dark:text-amber-300"
                  : "text-green-700 dark:text-green-300",
            )}
            data-testid="ontology-build-connection-state"
          >
            <span
              className="h-1.5 w-1.5 rounded-full bg-current"
              aria-hidden="true"
            />
            {isPaused ? "Paused" : connectionLabel}
          </span>
          <span className="text-xs font-semibold tabular-nums text-gray-500 dark:text-gray-400">
            {projection.latestEvent == null && !terminal
              ? "Restoring"
              : `${Math.round(progress)}%`}
          </span>
        </div>
      </div>

      {stream.errorMessage ? (
        <div
          role="status"
          className={cn(
            "border-b px-4 py-2 text-xs",
            "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300",
          )}
        >
          {stream.errorMessage}
        </div>
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-1 xl:grid-cols-[minmax(0,1fr)_390px]">
        <div className="relative min-h-[560px] overflow-hidden border-b border-gray-200 bg-gray-50 bg-[radial-gradient(#dcd7dc_1px,transparent_1px)] [background-size:22px_22px] dark:border-dark-border dark:bg-dark-bg/30 xl:border-b-0 xl:border-r">
          <div className="absolute left-4 top-4 z-20 flex flex-wrap items-center gap-2">
            <Badge variant="secondary">
              {entityProposals.length} proposed entities
            </Badge>
            <Badge variant="secondary">
              {relationshipProposals.length} proposed relationships
            </Badge>
          </div>

          <div
            className="hidden h-full min-h-[560px] sm:block"
            aria-label="Proposed ontology graph"
          >
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              {visibleRelationshipProposals.map((proposal) => {
                const source = entityPositionByRef.get(
                  proposal.sourceArtifactRef ?? "",
                );
                const target = entityPositionByRef.get(
                  proposal.targetArtifactRef ?? "",
                );
                if (!source || !target) return null;
                return (
                  <line
                    key={proposal.artifactRef}
                    data-relationship-ref={proposal.artifactRef}
                    x1={source.x}
                    y1={source.y}
                    x2={target.x}
                    y2={target.y}
                    vectorEffect="non-scaling-stroke"
                    className="stroke-blue-400 stroke-2 [stroke-dasharray:6_5] dark:stroke-blue-700"
                  />
                );
              })}
            </svg>
            {entityProposals.map((proposal, index) => (
              <ProposalNode
                key={proposal.artifactRef}
                proposal={proposal}
                className={NODE_POSITIONS[index].className}
              />
            ))}
            <BuildOrb
              progress={progress}
              label={ontologyBuildStageLabel(projection.stage)}
              completed={projection.isCompleted}
              failed={projection.isFailed}
              restoring={projection.latestEvent == null && !terminal}
            />
          </div>

          <div className="grid gap-3 px-4 pb-32 pt-16 sm:hidden">
            <BuildOrb
              progress={progress}
              label={ontologyBuildStageLabel(projection.stage)}
              completed={projection.isCompleted}
              failed={projection.isFailed}
              restoring={projection.latestEvent == null && !terminal}
              compact
            />
            {entityProposals.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-white/80 p-5 text-center text-sm text-gray-500 dark:border-dark-border dark:bg-dark-surface/80 dark:text-gray-400">
                Validated entity proposals will appear here as persisted events
                arrive.
              </div>
            ) : (
              entityProposals.map((proposal) => (
                <ProposalNode key={proposal.artifactRef} proposal={proposal} />
              ))
            )}
          </div>

          <div className="absolute bottom-4 left-4 right-4 z-20 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex max-w-xl items-start gap-2 rounded-lg border border-blue-200 bg-blue-50/95 px-3 py-2.5 text-xs leading-5 text-blue-800 shadow-sm backdrop-blur dark:border-blue-900 dark:bg-blue-950/80 dark:text-blue-200">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong>Draft only.</strong> Proposals enter steward review;
                runtime behavior changes only after pack validation and
                activation.
              </span>
            </div>
            {projection.isCompleted && onReviewChanges ? (
              <Button type="button" onClick={onReviewChanges}>
                <Check className="h-4 w-4" />
                Review changes
              </Button>
            ) : null}
          </div>
        </div>

        <aside
          className="flex min-h-[420px] flex-col bg-white dark:bg-dark-surface"
          aria-labelledby="ontology-build-feed-title"
        >
          <header className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-4 dark:border-dark-border">
            <div>
              <h2
                id="ontology-build-feed-title"
                className="font-subtitle text-xl text-charcoal dark:text-gray-100"
              >
                Build activity
              </h2>
              <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                Persisted operational evidence. No private model reasoning is
                displayed.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              aria-label={
                isPaused ? "Resume visual updates" : "Pause visual updates"
              }
              title={
                isPaused ? "Resume visual updates" : "Pause visual updates"
              }
              onClick={isPaused ? resumeVisualUpdates : () => setIsPaused(true)}
              disabled={terminal}
            >
              {isPaused ? (
                <Play className="h-4 w-4" />
              ) : (
                <Pause className="h-4 w-4" />
              )}
            </Button>
          </header>

          <div className="flex flex-wrap gap-3 border-b border-gray-200 px-4 py-2 text-[10px] text-gray-500 dark:border-dark-border dark:text-gray-400">
            <LegendDot className="bg-green-600" label="Validated" />
            <LegendDot className="bg-blue-600" label="Proposed" />
            <LegendDot className="bg-amber-600" label="Retry / attention" />
            <LegendDot className="bg-violet-600" label="Recovered" />
            <LegendDot className="bg-red-600" label="Failed" />
          </div>

          <ol
            className="min-h-0 flex-1 overflow-y-auto px-4 py-2"
            role="log"
            aria-live={isPaused ? "off" : "polite"}
            aria-relevant="additions"
            data-testid="ontology-build-activity-feed"
          >
            {projection.feed.length === 0 ? (
              <li className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                Waiting for the first persisted generation event.
              </li>
            ) : (
              projection.feed
                .slice(-80)
                .map((event) => (
                  <ActivityRow
                    key={String(event.event_id ?? event.id)}
                    event={event}
                  />
                ))
            )}
          </ol>
          <footer className="border-t border-gray-200 px-4 py-3 text-[10px] leading-4 text-gray-500 dark:border-dark-border dark:text-gray-400">
            Events replay by ID after reload, stream over SSE, and fall back to
            polling after disconnect.
          </footer>
        </aside>
      </div>

    </section>
  );
}

function BuildOrb({
  progress,
  label,
  completed,
  failed,
  restoring,
  compact = false,
}: {
  progress: number;
  label: string;
  completed: boolean;
  failed: boolean;
  restoring: boolean;
  compact?: boolean;
}) {
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress / 100);
  return (
    <div
      className={cn(
        "z-10 flex flex-col items-center",
        compact
          ? "relative mx-auto"
          : "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
      )}
      data-testid="ontology-build-orb"
    >
      <div className={cn("relative", compact ? "h-36 w-36" : "h-48 w-48")}>
        {!completed && !failed ? (
          <div className="absolute -inset-6 rounded-full bg-eliza-red/5 motion-safe:animate-pulse motion-reduce:animate-none" />
        ) : null}
        <svg
          className="absolute inset-0 h-full w-full -rotate-90"
          viewBox="0 0 160 160"
          aria-hidden="true"
        >
          <circle
            cx="80"
            cy="80"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            className="text-gray-200 dark:text-dark-border"
          />
          <circle
            cx="80"
            cy="80"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            className={cn(
              "transition-[stroke-dashoffset] duration-500 motion-reduce:transition-none",
              failed
                ? "text-red-500"
                : completed
                  ? "text-green-600"
                  : "text-eliza-red",
            )}
          />
        </svg>
        <div className="absolute inset-4 flex flex-col items-center justify-center rounded-full bg-white text-center shadow-md dark:bg-dark-surface">
          <strong className="font-title text-3xl font-normal text-charcoal dark:text-gray-100">
            {restoring ? "…" : `${Math.round(progress)}%`}
          </strong>
          <span className="mt-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-gray-400">
            {failed
              ? "Failed"
              : completed
                ? "Complete"
                : restoring
                  ? "Restoring"
                  : "Mapping"}
          </span>
        </div>
      </div>
      <span className="mt-3 max-w-[240px] rounded-full border border-gray-200 bg-white/95 px-3 py-1.5 text-center text-[11px] font-semibold text-gray-600 shadow-sm dark:border-dark-border dark:bg-dark-surface/95 dark:text-gray-300">
        {label}
      </span>
    </div>
  );
}

function ProposalNode({
  proposal,
  className,
}: {
  proposal: OntologyBuildProposal;
  className?: string;
}) {
  return (
    <article
      className={cn(
        "z-10 w-full rounded-lg border border-dashed border-blue-300 bg-white/95 p-3 shadow-sm dark:border-blue-700 dark:bg-dark-surface/95 sm:absolute sm:w-44",
        className,
      )}
      data-proposal-ref={proposal.artifactRef}
    >
      <div className="flex items-center justify-between gap-2 text-[9px] font-semibold uppercase tracking-[0.13em] text-gray-500 dark:text-gray-400">
        <span>Proposed {proposal.artifactType}</span>
        <span className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-300">
          <Sparkles className="h-3 w-3" />
          Review
        </span>
      </div>
      <h3 className="mt-2 truncate font-subtitle text-base text-charcoal dark:text-gray-100">
        {proposal.label}
      </h3>
      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2 text-[10px] text-gray-500 dark:border-dark-border dark:text-gray-400">
        <span>
          {proposal.sourceBindingCount != null
            ? `${proposal.sourceBindingCount} source binding${proposal.sourceBindingCount === 1 ? "" : "s"}`
            : "Persisted candidate"}
        </span>
        {proposal.confidenceScore != null ? (
          <strong>{Math.round(proposal.confidenceScore * 100)}%</strong>
        ) : null}
      </div>
    </article>
  );
}

function ActivityRow({ event }: { event: OntologyGenerationRunEvent }) {
  const kind = activityKind(event);
  const time = new Date(event.created_at);
  return (
    <li
      className="relative grid grid-cols-[28px_minmax(0,1fr)] gap-2 border-b border-gray-100 py-3 last:border-b-0 dark:border-dark-border"
      data-event-id={String(event.event_id ?? event.id)}
      data-activity-kind={kind}
    >
      <span
        className={cn(
          "mt-0.5 flex h-7 w-7 items-center justify-center rounded-full",
          kind === "failed"
            ? "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300"
            : kind === "attention"
              ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
              : kind === "recovered"
                ? "bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                : kind === "proposed"
                  ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                  : kind === "running"
                    ? "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                    : "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300",
        )}
        aria-hidden="true"
      >
        {kind === "failed" || kind === "attention" ? (
          <AlertTriangle className="h-3.5 w-3.5" />
        ) : kind === "recovered" ? (
          <ShieldCheck className="h-3.5 w-3.5" />
        ) : kind === "running" ? (
          <RefreshCw className="h-3.5 w-3.5 motion-safe:animate-spin motion-reduce:animate-none" />
        ) : kind === "proposed" ? (
          <Sparkles className="h-3.5 w-3.5" />
        ) : (
          <Check className="h-3.5 w-3.5" />
        )}
      </span>
      <div className="min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs font-semibold leading-5 text-charcoal dark:text-gray-100">
            {event.message}
          </p>
          <time
            className="shrink-0 font-mono text-[9px] text-gray-400"
            dateTime={event.created_at}
          >
            {Number.isNaN(time.getTime())
              ? ""
              : time.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
          </time>
        </div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {event.payload.confidence_score != null ? (
            <Badge variant="success">
              {Math.round(event.payload.confidence_score * 100)}% confidence
            </Badge>
          ) : null}
          {event.payload.retry_attempt != null ? (
            <Badge variant="warning">
              Retry {event.payload.retry_attempt} of{" "}
              {event.payload.retry_limit ?? "—"}
            </Badge>
          ) : null}
          {event.payload.evidence_refs?.slice(0, 2).map((evidence) => (
            <Badge key={`${evidence.type}:${evidence.ref}`} variant="secondary">
              {evidence.label ?? `${evidence.type} ${evidence.ref}`}
            </Badge>
          ))}
        </div>
      </div>
    </li>
  );
}

function activityKind(
  event: OntologyGenerationRunEvent,
): "failed" | "attention" | "recovered" | "proposed" | "running" | "validated" {
  if (event.event_type === "run_failed" || event.status === "failed") {
    return "failed";
  }
  if (
    event.event_type === "stage_retrying" ||
    event.event_type === "conflict_detected"
  ) {
    return "attention";
  }
  if (event.event_type === "stage_recovered") {
    return "recovered";
  }
  if (event.event_type === "candidate_proposed" || event.status === "proposed") {
    return "proposed";
  }
  if (event.event_type === "stage_started" || event.status === "running") {
    return "running";
  }
  return "validated";
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn("h-1.5 w-1.5 rounded-full", className)}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

function buildConnectionLabel(
  state: OntologyGenerationStreamState["connectionState"],
  wasRestored: boolean,
): string {
  if (state === "live") return wasRestored ? "Live · restored" : "Live";
  if (state === "polling") return "Polling";
  if (state === "recovered") return "Recovered";
  if (state === "completed") return "Completed";
  if (state === "failed") return "Failed";
  if (state === "permission_denied") return "Permission required";
  if (state === "connecting") return "Connecting";
  return "Waiting";
}
